import "./App.css";

import {
  createBrowserRouter,
  Route,
  createRoutesFromElements,
  RouterProvider,
} from "react-router-dom";
import { useState, createContext, useEffect, useRef } from "react";
import { io } from "socket.io-client";
import "bootstrap/dist/css/bootstrap.min.css";
import IncomingCallOverlay from "./components/chatComponents/IncomingCallOverlay";
import CalleeVideoOverlay from "./components/chatComponents/CalleeVideoOverlay";

// LOGIN PAGES
// import LandingPage from "./pages/LandingPage";
// import HomePage from "./pages/HomePage";
import LoginPage from "./pages/login/LoginPage";
import SignUpPage from "./pages/login/SignUpPage";
import ForgotPasswordPage from "./pages/login/ForgotPasswordPage";
import ResetPasswordPage from "./pages/login/ResetPasswordPage";
import EmailVerificationPage from "./pages/login/EmailVerificationPage";
import MobileVerificationPage from "./pages/login/MobileVerificationPage";
import EnterOTPPage from "./pages/login/EnterOTPPage";
import WelcomePage from "./pages/login/WelcomePage";
import NewUsernamePage from "./pages/login/NewUsernamePage";
import SelectAvatarPage from "./pages/login/SelectAvatarPage";

// MAIN PAGES
import MemoriesPage from "./pages/home/MemoriesPage";
import SearchPage from "./pages/home/SearchPage";
import ConnectPage from "./pages/home/ConnectPage";
import ChatPage from "./pages/home/ChatPage";
import ProfilePage from "./pages/home/ProfilePage";
import NotificationsPage from "./pages/home/NotificationsPage";
import FeedbackPage from "./pages/home/FeedbackPage";
import NotFound from "pages/NotFound";

// LAYOUTS
import RootLayout from "./layouts/RootLayout";
import LoginLayout from "./layouts/LoginLayout";

export const CurrentUserContext = createContext(null);
export const SocketContext = createContext({ socket: null, socketRef: null });

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:3080";

// Router must be created OUTSIDE the component to prevent full remounts on
// every App re-render (which would destroy socket listeners in child components).
const router = createBrowserRouter(
  createRoutesFromElements(
    <>
      {/* MAIN PAGE ROUTES */}
      <Route path="/" element={<RootLayout />}>
        {/* <Route index element={<HomePage />} /> */}
        <Route path="memories" element={<MemoriesPage />} />
        <Route path="search" element={<SearchPage />} />
        <Route path="connect" element={<ConnectPage />} />
        <Route path="chat" element={<ChatPage />} />
        <Route path="profile" element={<ProfilePage />} />
        <Route path="notifications" element={<NotificationsPage />} />
        <Route path="feedback" element={<FeedbackPage />} />
      </Route>

      {/* LOGIN PAGE ROUTES */}
      <Route path="login" element={<LoginLayout />}>
        <Route index element={<LoginPage />} />
        <Route path="signup" element={<SignUpPage />} />
        <Route path="forgotpassword" element={<ForgotPasswordPage />} />
        <Route path="resetPassword" element={<ResetPasswordPage />} />
        <Route path="emailVerification" element={<EmailVerificationPage />} />
        <Route
          path="mobileVerification"
          element={<MobileVerificationPage />}
        />
        <Route path="enterotp" element={<EnterOTPPage />} />
        <Route path="welcome" element={<WelcomePage />} />
        <Route path="newusername" element={<NewUsernamePage />} />
        <Route path="selectavatar" element={<SelectAvatarPage />} />
      </Route>
      <Route path="*" element={<NotFound />} />
    </>
  )
);

function App() {
  const [currentUser, setCurrentUser] = useState(null);
  const [globalSocket, setGlobalSocket] = useState(null);
  const [incomingCall, setIncomingCall] = useState(null);
  const [activeCallInfo, setActiveCallInfo] = useState(null);
  const [connectToast, setConnectToast] = useState(null); // { username, userId }
  const globalSocketRef = useRef(null);

  // Global socket — connects once user is authenticated, registers for direct calls
  useEffect(() => {
    const userId = currentUser?.user_id;
    if (!userId) return;

    // Don't recreate if socket already exists and is connected
    if (globalSocketRef.current) {
      // Ensure state is in sync with ref
      setGlobalSocket(globalSocketRef.current);
      // Re-register in case user_id changed
      if (globalSocketRef.current.connected) {
        globalSocketRef.current.emit("registerUser", userId);
      }
      return;
    }

    const sock = io(API_URL, { transports: ["websocket", "polling"] });
    globalSocketRef.current = sock;
    setGlobalSocket(sock);

    sock.on("connect", () => {
      sock.emit("registerUser", userId);
    });

    sock.on("incomingCall", ({ callerSocketId, callerUsername, callerUserId, callType }) => {
      setIncomingCall({ callerSocketId, callerUsername, callerUserId, callType: callType || "video" });
    });

    sock.on("followerOnConnect", ({ username, userId }) => {
      setConnectToast({ username, userId });
      // Auto-dismiss after 8 seconds
      setTimeout(() => setConnectToast(null), 8000);
    });

    return () => {
      // Only clean up on unmount (App unmounting = page closing)
    };
  }, [currentUser?.user_id]);

  useEffect(() => {
    const storedUser = localStorage.getItem("currentUser");
    const storedUserObject = storedUser ? JSON.parse(storedUser) : null;
    // If the token/email does not exist
    if (!storedUserObject?.token || !storedUserObject?.email) {
      if (!currentUser?.token || !currentUser?.email) {
        return;
      }
    }
    // If user_id is missing from stored session, extract from JWT
    const userObj = currentUser || storedUserObject;
    if (userObj?.token && !userObj.user_id) {
      try {
        const payload = JSON.parse(atob(userObj.token.split(".")[1]));
        if (payload.user_id) {
          userObj.user_id = payload.user_id;
          localStorage.setItem("currentUser", JSON.stringify(userObj));
          if (currentUser) setCurrentUser({ ...currentUser, user_id: payload.user_id });
        }
      } catch {}
    }

    // If the token exists, verify it with the auth server to see if it is valid
    fetch(`${API_URL}/verify`, {
      method: "POST",
      headers: {
        "jwt-token": currentUser?.token
          ? currentUser.token
          : storedUserObject?.token,
      },
    })
      .then((r) => r.json())
      .then((r) => {
        // token valid
        if ("success" === r.message) {
          currentUser
            ? localStorage.setItem("currentUser", JSON.stringify(currentUser))
            : setCurrentUser(storedUserObject);
        } else {
          localStorage.removeItem("currentUser");
          setCurrentUser(null);
        }
      })
      .catch(() => {
        // Network/CORS error — trust stored session if available
        if (storedUserObject?.token) {
          setCurrentUser(storedUserObject);
        }
      });
  }, [currentUser]);

  return (
    <CurrentUserContext.Provider value={{ currentUser, setCurrentUser }}>
      <SocketContext.Provider value={{ socket: globalSocket, socketRef: globalSocketRef }}>
        <div className="App">
          <RouterProvider router={router} />

          {/* Global incoming call overlay — shows on any page */}
          {incomingCall && !activeCallInfo && (
            <IncomingCallOverlay
              callerUsername={incomingCall.callerUsername}
              callType={incomingCall.callType}
              onAccept={() => {
                globalSocket.emit("acceptCall", { callerSocketId: incomingCall.callerSocketId });
                setActiveCallInfo({ callerSocketId: incomingCall.callerSocketId, callerUsername: incomingCall.callerUsername, callType: incomingCall.callType });
                setIncomingCall(null);
              }}
              onReject={() => {
                globalSocket.emit("rejectCall", { callerSocketId: incomingCall.callerSocketId });
                setIncomingCall(null);
              }}
            />
          )}

          {/* Global callee video overlay */}
          {activeCallInfo && globalSocket && (
            <CalleeVideoOverlay
              socket={globalSocket}
              callerSocketId={activeCallInfo.callerSocketId}
              callerUsername={activeCallInfo.callerUsername}
              callType={activeCallInfo.callType}
              onEnd={() => setActiveCallInfo(null)}
            />
          )}

          {/* Toast: follower is on Connect page */}
          {connectToast && (
            <div
              style={{
                position: "fixed",
                bottom: 90,
                left: "50%",
                transform: "translateX(-50%)",
                backgroundColor: "#1e1e2e",
                border: "1px solid var(--main-purple)",
                borderRadius: 16,
                padding: "12px 20px",
                display: "flex",
                alignItems: "center",
                gap: 12,
                zIndex: 3000,
                boxShadow: "0 4px 20px rgba(0,0,0,0.5)",
                animation: "slideUp 0.3s ease-out",
              }}
            >
              <ion-icon name="videocam" style={{ color: "var(--main-purple)", fontSize: "1.5rem" }}></ion-icon>
              <span style={{ color: "#fff", fontSize: "1rem" }}>
                <strong style={{ color: "var(--main-purple)" }}>@{connectToast.username}</strong> is on Connect
              </span>
              <button
                onClick={() => { setConnectToast(null); window.location.href = "/connect"; }}
                style={{
                  backgroundColor: "var(--main-purple)",
                  color: "#101010",
                  border: "none",
                  borderRadius: 20,
                  padding: "6px 16px",
                  fontWeight: 600,
                  fontSize: "0.85rem",
                  cursor: "pointer",
                  marginLeft: 4,
                }}
              >
                Join
              </button>
              <button
                onClick={() => setConnectToast(null)}
                style={{ background: "none", border: "none", color: "#666", cursor: "pointer", fontSize: "1.2rem", padding: 0 }}
              >
                <ion-icon name="close"></ion-icon>
              </button>
              <style>{`@keyframes slideUp { from { transform: translateX(-50%) translateY(20px); opacity: 0; } to { transform: translateX(-50%) translateY(0); opacity: 1; } }`}</style>
            </div>
          )}
        </div>
      </SocketContext.Provider>
    </CurrentUserContext.Provider>
  );
}

export default App;
